export interface CorrectionPoint {
  x: number;
  y: number;
}

/** Corrections below normal 30Hz travel are part of authoritative motion. */
export const CORRECTION_THRESHOLD = 24;
/** A large network correction is reconciled over this short visual window. */
export const CORRECTION_DURATION_MS = 180;
const MIN_TRAVEL_STEP = 0.35;

export function correctionForJump(
  displayed: CorrectionPoint,
  authoritative: CorrectionPoint,
): CorrectionPoint {
  const x = displayed.x - authoritative.x;
  const y = displayed.y - authoritative.y;
  if (Math.hypot(x, y) <= CORRECTION_THRESHOLD) return { x: 0, y: 0 };
  return { x, y };
}

export function correctedPoint(
  authoritative: CorrectionPoint,
  correction: CorrectionPoint,
  elapsedMs: number,
): CorrectionPoint {
  const remaining = Math.max(0, 1 - Math.max(0, elapsedMs) / CORRECTION_DURATION_MS);
  if (remaining === 0) return { x: authoritative.x, y: authoritative.y };
  return {
    x: authoritative.x + correction.x * remaining,
    y: authoritative.y + correction.y * remaining,
  };
}

export function boundedTravelPoint(
  previous: CorrectionPoint,
  desired: CorrectionPoint,
  origin: CorrectionPoint,
  status: string,
): CorrectionPoint {
  if (status !== 'EXTENDING' && status !== 'RETRACTING') return desired;
  const dx = desired.x - origin.x;
  const dy = desired.y - origin.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) return desired;
  const previousLength = Math.hypot(previous.x - origin.x, previous.y - origin.y);
  const targetLength = status === 'EXTENDING'
    ? Math.max(length, previousLength + MIN_TRAVEL_STEP)
    : Math.min(length, Math.max(0, previousLength - MIN_TRAVEL_STEP));
  if (targetLength === length) return desired;
  return {
    x: origin.x + dx / length * targetLength,
    y: origin.y + dy / length * targetLength,
  };
}
